// /home/bigbrain/projects/smashparty/src/client/ui/components/number-stepper.ts
/**
 * Plan 23a, Task 4.3 — Number Stepper primitive
 *
 * Factory: createNumberStepper(config): UIComponentHandle & { value: number; setValue(v: number): void }
 *
 * Layout: [-] (ghost icon button) — value display (h4 headline, 40px min-width) — [+] (ghost icon)
 * Composes createButton from Task 2.1 with variant:'ghost' + size:'icon'.
 *
 * Design system references:
 *   - docs/design/tokens.json  — color, typography, spacing values
 *   - docs/design/components.md §22  — Number Stepper construction
 */

import { createButton } from './button';
import type { UIComponentHandle, UIComponentConfig } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface StepperOpts {
  /** Minimum allowed value (inclusive). */
  min: number;
  /** Maximum allowed value (inclusive). */
  max: number;
  /** Increment/decrement step. Default: 1. */
  step?: number;
  /** Initial value. Defaults to min when omitted. */
  value?: number;
  /** Called with the new value when the user clicks + or -. NOT called by setValue(). */
  onChange?: (v: number) => void;
}

export interface StepperHandle extends UIComponentHandle {
  /** Current numeric value. */
  readonly value: number;
  /** Programmatically set value — clamps to [min, max]. Does NOT fire onChange. */
  setValue(v: number): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createNumberStepper(config: UIComponentConfig & StepperOpts): StepperHandle {
  const { gsapRegistry, mount, min, max, step = 1, onChange } = config;
  const initialValue =
    config.value !== undefined ? Math.max(min, Math.min(max, config.value)) : min;

  let currentValue = initialValue;
  let disposed = false;

  // ── Container row ──────────────────────────────────────────────────────────

  const container = document.createElement('div');
  container.className = 'ui-number-stepper';
  container.style.cssText = [
    `display: inline-flex`,
    `align-items: center`,
    `gap: var(--spacing-sm, 8px)`,
  ].join('; ');

  // ── Value display ──────────────────────────────────────────────────────────
  // tokens: typography.family.headline h4, color.text.primary, min-width 40px, centered

  const display = document.createElement('span');
  display.className = 'ui-number-stepper__value';
  display.dataset.stepperValue = '';
  display.style.cssText = [
    `font-family: var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)`,
    `font-size: var(--typography-size-h4, 22px)`,
    `font-weight: var(--typography-weight-bold, 700)`,
    `color: var(--color-text-primary, #FFFFFF)`,
    `min-width: 40px`,
    `text-align: center`,
    `user-select: none`,
    `line-height: 1`,
  ].join('; ');
  display.textContent = String(currentValue);

  // ── Helper: update disabled state on both buttons ─────────────────────────

  function syncButtonStates(): void {
    const atMin = currentValue <= min;
    const atMax = currentValue >= max;

    // Mirror disabled state via aria-disabled + opacity (ghost buttons use same attr)
    const minusEl = minusHandle.element;
    const plusEl = plusHandle.element;

    if (atMin) {
      minusEl.setAttribute('aria-disabled', 'true');
      minusEl.setAttribute('disabled', '');
      minusEl.style.opacity = '0.55';
      minusEl.style.cursor = 'not-allowed';
      minusEl.style.pointerEvents = 'none';
    } else {
      minusEl.removeAttribute('aria-disabled');
      minusEl.removeAttribute('disabled');
      minusEl.style.opacity = '1';
      minusEl.style.cursor = 'pointer';
      minusEl.style.pointerEvents = 'auto';
    }

    if (atMax) {
      plusEl.setAttribute('aria-disabled', 'true');
      plusEl.setAttribute('disabled', '');
      plusEl.style.opacity = '0.55';
      plusEl.style.cursor = 'not-allowed';
      plusEl.style.pointerEvents = 'none';
    } else {
      plusEl.removeAttribute('aria-disabled');
      plusEl.removeAttribute('disabled');
      plusEl.style.opacity = '1';
      plusEl.style.cursor = 'pointer';
      plusEl.style.pointerEvents = 'auto';
    }
  }

  // ── [-] Button ─────────────────────────────────────────────────────────────

  const minusHandle = createButton({
    gsapRegistry,
    variant: 'ghost',
    size: 'icon',
    label: '−',
    disabled: false,
    onClick(): void {
      if (disposed) return;
      if (currentValue <= min) return;
      currentValue = Math.max(min, currentValue - step);
      display.textContent = String(currentValue);
      syncButtonStates();
      if (onChange) onChange(currentValue);
    },
  });

  const minusEl = minusHandle.element;
  minusEl.setAttribute('data-action', 'stepper-decrement');
  minusEl.style.width = '48px';
  minusEl.style.height = '48px';
  minusEl.style.minHeight = '48px';
  minusEl.style.padding = '0';

  // ── [+] Button ─────────────────────────────────────────────────────────────

  const plusHandle = createButton({
    gsapRegistry,
    variant: 'ghost',
    size: 'icon',
    label: '+',
    disabled: false,
    onClick(): void {
      if (disposed) return;
      if (currentValue >= max) return;
      currentValue = Math.min(max, currentValue + step);
      display.textContent = String(currentValue);
      syncButtonStates();
      if (onChange) onChange(currentValue);
    },
  });

  const plusEl = plusHandle.element;
  plusEl.setAttribute('data-action', 'stepper-increment');
  plusEl.style.width = '48px';
  plusEl.style.height = '48px';
  plusEl.style.minHeight = '48px';
  plusEl.style.padding = '0';

  // ── Assemble container: [-] display [+] ───────────────────────────────────

  container.appendChild(minusEl);
  container.appendChild(display);
  container.appendChild(plusEl);

  // Set initial disabled states via syncButtonStates
  // Note: createButton already set aria-disabled when disabled:true,
  // but we still call sync to normalise cursor/pointer-events for non-disabled side.
  syncButtonStates();

  // ── Mount ──────────────────────────────────────────────────────────────────

  if (mount) {
    mount.appendChild(container);
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    minusHandle.dispose();
    plusHandle.dispose();

    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }

  // ── Return handle ──────────────────────────────────────────────────────────

  return {
    element: container,

    get value(): number {
      return currentValue;
    },

    setValue(v: number): void {
      currentValue = Math.max(min, Math.min(max, v));
      display.textContent = String(currentValue);
      syncButtonStates();
      // Does NOT fire onChange per spec
    },

    dispose,
  };
}
